import {FinancialDisclosure,continueCheckout,type FinancialAcceptance} from './FinancialDisclosure';
import { useMemo, useState, type FormEvent } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import { toZoned } from '@shared/time';
import { useI18n } from '../lib/i18n';
import { SmsConsent } from './SmsConsent';
import { browserTimeZone, formatLocalDate, formatTime, zoneLabel } from '../lib/format';
import type { AppointmentResponse, CustomerDetails, PublicSession } from '../types';

interface Props {
  slug: string;
  sessions: PublicSession[];
  serviceName: string;
  details: CustomerDetails;
  onBooked: (result: AppointmentResponse) => void;
  /**
   * The seat counts on screen are wrong: somebody took the last place
   * while this customer was typing. Asks for a fresh list, so they are
   * not sent back to a class that still claims to have room.
   */
  onStale: () => void;
}

/**
 * Booking a class is not booking a slot: the time is already fixed and
 * the only question is whether there is room. So the customer picks the
 * class itself, then gives their details — no calendar in between.
 */
export function ClassPicker({ slug, sessions, serviceName, details, onBooked, onStale }: Props) {
  const { t, locale } = useI18n();
  const customerZone = useMemo(browserTimeZone, []);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [name, setName] = useState(details.name);
  const [email, setEmail] = useState(details.email);
  const [phone, setPhone] = useState(details.phone ?? '');
  const [notes, setNotes] = useState('');
  const [smsOptIn,setSmsOptIn]=useState(false);
  const [financial,setFinancial]=useState<FinancialAcceptance>({quote:null,accepted:false,ready:false,priceKind:'card'});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const byDay = useMemo(() => {
    const days = new Map<string, PublicSession[]>();
    for (const s of [...sessions].sort((a, b) => a.starts_at.localeCompare(b.starts_at))) {
      const day = toZoned(customerZone, s.starts_at).date;
      days.set(day, [...(days.get(day) ?? []), s]);
    }
    return [...days.entries()];
  }, [sessions, customerZone]);

  const chosen = sessions.find((s) => s.id === sessionId) ?? null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!chosen) return;
    setError(null);
    if (!name.trim() || !email.trim()) {
      setError(t('classes.need_name_email'));
      return;
    }
    setBusy(true);
    try {
      const result = await api<AppointmentResponse>(`/public/${slug}/sessions/seats`, {
        method: 'POST',
        json: {
          financial_policy_token:financial.quote?.token,accept_financial_policy:financial.accepted,
      // The PATH the customer chose (0044); the server prices it.
      price_kind:financial.priceKind,
          session_id: chosen.id,
          notes: notes.trim() === '' ? null : notes.trim(),
          customer: {
            sms_opt_in:smsOptIn,
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim() === '' ? null : phone.trim(),
            timezone: customerZone,
            locale,
          },
        },
      });
      if(await continueCheckout(slug,result))return;
      onBooked(result);
    } catch (err) {
      // A class filling up while somebody types is ordinary, not an error
      // to apologise for — send them back to the list with the reason.
      setError(err instanceof ApiRequestError ? err.message : t('classes.could_not_book'));
      if (err instanceof ApiRequestError && err.code === 'conflict') {
        setSessionId(null);
        onStale();
      }
    } finally {
      setBusy(false);
    }
  }

  if (sessions.length === 0) {
    return (
      <>
        {/* Keep the reason when the last place going is what emptied the
            list: "no classes open" alone would read as our mistake. */}
        {error && <p className="error" role="alert">{error}</p>}
        <p className="empty">{t('classes.none_open', { service: serviceName })}</p>
      </>
    );
  }

  return (
    <div className="classes">
      <p className="zone-note">{t('classes.times_in_zone', { zone: zoneLabel(customerZone) })}</p>
      {error && <p className="error" role="alert">{error}</p>}

      {byDay.map(([day, list]) => (
        <div key={day} className="class-day">
          <h4>{formatLocalDate(day, {}, locale)}</h4>
          <div className="class-list">
            {list.map((s) => {
              const active = s.id === sessionId;
              const nearlyFull = s.seats_left <= 2;
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`class-btn${active ? ' is-selected' : ''}`}
                  aria-pressed={active}
                  onClick={() => setSessionId(active ? null : s.id)}
                >
                  <span className="class-time">{formatTime(customerZone, s.starts_at, locale)}</span>
                  <span className={nearlyFull ? 'class-seats class-seats--low' : 'class-seats'}>
                    {s.seats_left === 1 ? t('classes.place_left') : t('classes.places_left', { count: s.seats_left })}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {chosen && (
        <form className="class-details" onSubmit={submit} noValidate>
          <h4>{t('booking.step_details')}</h4>
          <p className="hint">
            {t('classes.on_at', {
              service: serviceName,
              date: formatLocalDate(toZoned(customerZone, chosen.starts_at).date, {}, locale),
              time: formatTime(customerZone, chosen.starts_at, locale),
            })}
          </p>
          <div className="field">
            <label htmlFor="cl-name">{t('booking.full_name')}</label>
            <input id="cl-name" type="text" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="cl-email">{t('booking.email')}</label>
            <input id="cl-email" type="email" inputMode="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            <p className="hint">{t('classes.email_hint')}</p>
          </div>
          <div className="field">
            <label htmlFor="cl-phone">{t('booking.phone_label')} <span className="muted">{t('booking.optional_marker')}</span></label>
            <input id="cl-phone" type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            {phone.trim() !== '' && <SmsConsent checked={smsOptIn} onChange={setSmsOptIn} />}
          </div>
          <div className="field">
            <label htmlFor="cl-notes">{t('booking.notes')} <span className="muted">{t('booking.optional_marker')}</span></label>
            <textarea id="cl-notes" rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="actions">
            <FinancialDisclosure slug={slug} query={new URLSearchParams({session_id:chosen.id}).toString()} onChange={setFinancial}/>
          <button type="submit" className="btn btn-primary" disabled={busy||!financial.ready||!financial.accepted}>
              {busy ? t('classes.booking_place') : t('classes.book_my_place')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

